import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, Heading, VStack, HStack, Input, Button, Text, Flex, Progress, useToast, Checkbox,
    CheckboxGroup, SimpleGrid, Icon, Alert, AlertIcon, AlertTitle, AlertDescription, Stack,
    Radio, RadioGroup, Badge, Spinner, Divider
} from '@chakra-ui/react';
import { FaFileCsv, FaCheckCircle, FaExclamationTriangle, FaDownload, FaSyncAlt } from 'react-icons/fa';
import { startConversaExport, getConversaExportAtendentes, getExportStatus, downloadExportFile } from "../services/api";

const DEPARTAMENTOS_PADRAO = [
    { id: 158624, nome: 'Comercial' },
    { id: 158625, nome: 'Suporte' },
    { id: 158626, nome: 'Financeiro' },
];

const hoje = () => new Date().toISOString().slice(0, 10);
const diasAtras = (dias) => new Date(Date.now() - dias * 86400000).toISOString().slice(0, 10);

const ExportarConversas = () => {
    const [filters, setFilters] = useState({
        data_inicial: diasAtras(6),
        data_final: hoje(),
        departamentos: [],
        atendentes: [],
        criterio_atendente: 'responsavel',
        status: 'todas',
        incluir_bot: true,
        incluir_sistema: false,
        formato: 'mensagem',
    });

    const [atendentes, setAtendentes] = useState([]);
    const [departamentos, setDepartamentos] = useState(DEPARTAMENTOS_PADRAO);
    const [carregandoAtendentes, setCarregandoAtendentes] = useState(false);
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);

    const toast = useToast();
    const intervalRef = useRef(null);

    const carregarAtendentes = useCallback(async (atualizar = false) => {
        setCarregandoAtendentes(true);
        try {
            const data = await getConversaExportAtendentes(atualizar);
            setAtendentes(data.data || []);
            if (data.departamentos?.length) {
                setDepartamentos(data.departamentos);
            }
            if (atualizar) {
                toast({
                    title: "Lista de atendentes atualizada",
                    description: `${(data.data || []).length} atendente(s) encontrado(s) na Poli.`,
                    status: "success",
                    duration: 4000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "Não foi possível carregar os atendentes",
                description: error.response?.data?.detalhes || "Verifique a integração com a Poli e tente novamente.",
                status: "error",
                duration: 7000,
                isClosable: true,
            });
        } finally {
            setCarregandoAtendentes(false);
        }
    }, [toast]);

    useEffect(() => {
        carregarAtendentes(false);
    }, [carregarAtendentes]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const setFiltro = (campo, valor) => setFilters(prev => ({ ...prev, [campo]: valor }));

    const todosAtendentes = filters.atendentes.length === 0;

    const handleStartExport = async () => {
        if (!filters.data_inicial || !filters.data_final) {
            toast({
                title: "Informe o período",
                description: "A data inicial e a data final são obrigatórias.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        setIsGenerating(true);
        setJobStatus(null);
        setJobId(null);

        try {
            const data = await startConversaExport(filters);
            setJobId(data.job_id);
            toast({
                title: "Exportação iniciada!",
                description: "As conversas são lidas da Poli uma a uma, então o arquivo pode demorar alguns minutos.",
                status: "info",
                duration: 6000,
                isClosable: true,
            });
        } catch (error) {
            const erros = error.response?.data?.errors;
            toast({
                title: "Erro ao iniciar a exportação",
                description: erros ? Object.values(erros).flat().join(' ') : (error.response?.data?.message || "Ocorreu um erro inesperado."),
                status: "error",
                duration: 9000,
                isClosable: true,
            });
            setIsGenerating(false);
        }
    };

    const fetchStatus = useCallback(async () => {
        if (!jobId) return;

        try {
            const statusData = await getExportStatus(jobId);
            setJobStatus(statusData);

            if (['completed', 'failed'].includes(statusData.status)) {
                setIsGenerating(false);
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                if (statusData.status === 'completed') {
                    toast({
                        title: "Arquivo Pronto!",
                        description: "Sua exportação de conversas está pronta para download.",
                        status: "success",
                        duration: 7000,
                        isClosable: true,
                    });
                }
            }
        } catch (error) {
            console.error("Erro ao buscar status:", error);
            setIsGenerating(false);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
    }, [jobId, toast]);

    useEffect(() => {
        if (isGenerating && jobId) {
            if (!intervalRef.current) {
                intervalRef.current = setInterval(fetchStatus, 5000);
            }
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isGenerating, jobId, fetchStatus]);

    const handleDownload = async () => {
        if (!jobStatus?.file_name) return;

        try {
            await downloadExportFile(jobStatus.file_name);
            toast({ title: "Download Iniciado", status: "success", duration: 3000, isClosable: true });
        } catch (error) {
            toast({
                title: "Erro no Download",
                description: "Não foi possível baixar o arquivo. Por favor, gere um novo relatório.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setJobStatus(null);
            setJobId(null);
        }
    };

    const renderStatus = () => {
        if (!jobStatus) return null;
        switch (jobStatus.status) {
            case 'pending':
            case 'running':
                return (
                    <Box w="100%">
                        <Flex justify="space-between" mb={1}>
                            <Text fontSize="sm" fontWeight="medium">{jobStatus.message}</Text>
                            <Text fontSize="sm" fontWeight="bold" color="blue.600">{jobStatus.progress || 0}%</Text>
                        </Flex>
                        <Progress value={jobStatus.progress} size="md" colorScheme="blue" borderRadius="md" isIndeterminate={jobStatus.status === 'pending'} />
                    </Box>
                );
            case 'completed':
                return (
                    <Alert status="success" borderRadius="md" variant="left-accent">
                        <AlertIcon as={FaCheckCircle} />
                        <Box flex="1">
                            <AlertTitle>Arquivo Pronto!</AlertTitle>
                            <AlertDescription>
                                <Text fontSize="sm" mb={2}>{jobStatus.message}</Text>
                                <Button leftIcon={<FaDownload />} colorScheme="green" variant="solid" onClick={handleDownload} size="sm">
                                    Baixar Planilha
                                </Button>
                            </AlertDescription>
                        </Box>
                    </Alert>
                );
            case 'failed':
                return (
                    <Alert status="error" borderRadius="md" variant="left-accent">
                        <AlertIcon as={FaExclamationTriangle} />
                        <Box flex="1">
                            <AlertTitle>Falha na Exportação!</AlertTitle>
                            <AlertDescription fontSize="sm">{jobStatus.message || 'Ocorreu um erro inesperado.'}</AlertDescription>
                        </Box>
                    </Alert>
                );
            default: return null;
        }
    };

    return (
        <Box ml={{ base: 0, lg: '60px' }} p={{ base: 4, md: 8 }} bg="gray.50" minH="100vh">
            <VStack spacing={6} align="stretch" maxW="5xl" mx="auto">
                <Box>
                    <Heading justify="center" color={"#F68633"} size="lg">Exportar Conversas da Poli</Heading>
                    <Text fontSize="sm" color="gray.600" mt={2}>
                        As conversas são lidas direto da API da Poli no momento da exportação — nada fica salvo aqui.
                        Períodos longos levam alguns minutos; o arquivo fica pronto para download nesta tela.
                    </Text>
                </Box>

                <Box p={6} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Heading size="md" mb={4}>1. Período e Departamentos</Heading>
                    <Stack spacing={6}>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <Box>
                                <Text mb={1} fontSize="sm" fontWeight="medium">Data Inicial</Text>
                                <Input type="date" name="data_inicial" value={filters.data_inicial} onChange={handleFilterChange} />
                            </Box>
                            <Box>
                                <Text mb={1} fontSize="sm" fontWeight="medium">Data Final</Text>
                                <Input type="date" name="data_final" value={filters.data_final} onChange={handleFilterChange} />
                            </Box>
                        </SimpleGrid>
                        <Text fontSize="xs" color="gray.500" mt={-3}>
                            O período filtra as mensagens: uma conversa antiga que recebeu mensagem dentro das datas entra,
                            mas só com as mensagens do período. Máximo de 92 dias por exportação.
                        </Text>

                        <Box>
                            <Heading size="sm" mb={3}>Departamentos</Heading>
                            <CheckboxGroup
                                colorScheme="orange"
                                value={filters.departamentos}
                                onChange={(v) => setFiltro('departamentos', v.map(Number))}
                            >
                                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                                    {departamentos.map(d => (
                                        <Checkbox key={d.id} value={d.id}>{d.nome}</Checkbox>
                                    ))}
                                </SimpleGrid>
                            </CheckboxGroup>
                            <Text fontSize="xs" color="gray.500" mt={2}>
                                Nenhum marcado = todos os departamentos.
                            </Text>
                        </Box>

                        <Box>
                            <Heading size="sm" mb={3}>Status da Conversa</Heading>
                            <RadioGroup colorScheme="orange" value={filters.status} onChange={(v) => setFiltro('status', v)}>
                                <HStack spacing={6}>
                                    <Radio value="todas">Todas</Radio>
                                    <Radio value="abertas">Abertas</Radio>
                                    <Radio value="fechadas">Fechadas</Radio>
                                </HStack>
                            </RadioGroup>
                        </Box>
                    </Stack>
                </Box>

                <Box p={6} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
                        <Heading size="md">2. Atendentes</Heading>
                        <Button
                            size="xs"
                            variant="outline"
                            colorScheme="orange"
                            leftIcon={<Icon as={FaSyncAlt} />}
                            onClick={() => carregarAtendentes(true)}
                            isLoading={carregandoAtendentes}
                        >
                            Atualizar lista
                        </Button>
                    </Flex>

                    {carregandoAtendentes && atendentes.length === 0 ? (
                        <HStack color="gray.500" fontSize="sm"><Spinner size="sm" /><Text>Buscando atendentes na Poli...</Text></HStack>
                    ) : atendentes.length === 0 ? (
                        <Text fontSize="sm" color="gray.500">
                            Nenhum atendente encontrado. Use "Atualizar lista" para varrer os contatos recentes da Poli.
                        </Text>
                    ) : (
                        <>
                            <Checkbox
                                colorScheme="orange"
                                isChecked={todosAtendentes}
                                onChange={(e) => { if (e.target.checked) setFiltro('atendentes', []); }}
                                mb={3}
                                fontWeight="bold"
                            >
                                Todos os atendentes
                            </Checkbox>
                            <CheckboxGroup
                                colorScheme="orange"
                                value={filters.atendentes}
                                onChange={(v) => setFiltro('atendentes', v.map(Number))}
                            >
                                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                                    {atendentes.map(a => (
                                        <Checkbox key={a.poli_user_id} value={a.poli_user_id}>
                                            <HStack spacing={2}>
                                                <Text fontSize="sm">{a.nome}</Text>
                                                {a.departamentos?.map(d => (
                                                    <Badge key={d} colorScheme="gray" fontSize="0.6rem">{d}</Badge>
                                                ))}
                                            </HStack>
                                        </Checkbox>
                                    ))}
                                </SimpleGrid>
                            </CheckboxGroup>

                            <Divider my={4} />

                            <Heading size="sm" mb={2}>Como filtrar por atendente</Heading>
                            <RadioGroup
                                colorScheme="orange"
                                value={filters.criterio_atendente}
                                onChange={(v) => setFiltro('criterio_atendente', v)}
                                isDisabled={todosAtendentes}
                            >
                                <Stack spacing={2}>
                                    <Radio value="responsavel">
                                        <Text fontSize="sm">Conversas em que ele é o responsável <Text as="span" color="gray.500">(mais rápido)</Text></Text>
                                    </Radio>
                                    <Radio value="participou">
                                        <Text fontSize="sm">Conversas em que ele respondeu alguma mensagem <Text as="span" color="gray.500">(mais lento, varre todas as conversas do período)</Text></Text>
                                    </Radio>
                                </Stack>
                            </RadioGroup>
                            <Text fontSize="xs" color="gray.500" mt={2}>
                                Na Poli a conversa fica atribuída a um responsável, mas quem responde nem sempre é ele.
                                A coluna AUTOR da planilha sempre mostra quem escreveu cada mensagem.
                            </Text>
                        </>
                    )}
                </Box>

                <Box p={6} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Heading size="md" mb={4}>3. Formato do Arquivo</Heading>
                    <RadioGroup colorScheme="orange" value={filters.formato} onChange={(v) => setFiltro('formato', v)}>
                        <Stack spacing={2}>
                            <Radio value="mensagem">
                                <Text fontSize="sm">Uma linha por mensagem <Text as="span" color="gray.500">(data/hora, origem, autor, tipo, texto e anexo)</Text></Text>
                            </Radio>
                            <Radio value="conversa">
                                <Text fontSize="sm">Uma linha por conversa <Text as="span" color="gray.500">(contadores e a transcrição inteira numa célula)</Text></Text>
                            </Radio>
                        </Stack>
                    </RadioGroup>

                    <Divider my={4} />

                    <Stack spacing={2}>
                        <Checkbox
                            colorScheme="orange"
                            isChecked={filters.incluir_bot}
                            onChange={(e) => setFiltro('incluir_bot', e.target.checked)}
                        >
                            <Text fontSize="sm">Incluir mensagens do bot de atendimento</Text>
                        </Checkbox>
                        <Checkbox
                            colorScheme="orange"
                            isChecked={filters.incluir_sistema}
                            onChange={(e) => setFiltro('incluir_sistema', e.target.checked)}
                        >
                            <Text fontSize="sm">
                                Incluir eventos do sistema
                                <Text as="span" color="gray.500"> (chat finalizado, transferências, código de verificação do site)</Text>
                            </Text>
                        </Checkbox>
                    </Stack>
                </Box>

                <Box p={6} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Heading size="md" mb={4}>4. Gere o Arquivo</Heading>
                    <VStack spacing={4}>
                        <Button
                            leftIcon={<Icon as={FaFileCsv} />}
                            colorScheme="orange"
                            onClick={handleStartExport}
                            isLoading={isGenerating}
                            loadingText="Gerando..."
                            isDisabled={isGenerating}
                            w="full"
                            size="lg"
                        >
                            Gerar Arquivo de Conversas
                        </Button>
                        {renderStatus()}
                    </VStack>
                </Box>
            </VStack>
        </Box>
    );
};

export default ExportarConversas;
